import {
  FLAG_PAUSED,
  type OtherState,
  type RaceLeader,
  type RacePhase,
  type RaceStateMessage,
  type SelfState,
  type Snapshot,
  type Spawn,
  type WelcomeMessage,
} from "@cimara/protocol";
import { DEFAULT_RIDER_CONFIG, solveSpeed } from "@cimara/sim";
import type { World } from "@cimara/world";
import { clamp } from "../lib/format";
import {
  advanceRoute,
  createRouteIndex,
  lapDelta,
  samplePose,
  type Pose,
  type RouteIndex,
} from "../lib/route";
import type { ConnectionState } from "../net/socket";

/** How long the client trusts a snapshot before locally extrapolating. */
const SNAPSHOT_FRESH_MS = 1200;
/** Other riders are rendered one snapshot behind (100 ms) for interpolation. */
const INTERPOLATION_DELAY_MS = 100;
/** A ring may update less often than the snapshot; keep its last state this long. */
const RIDER_TRACK_TTL_MS = 4000;
/** Enough for the 20-bot pack plus a busy crowd without unbounded draw calls. */
export const MAX_RENDERED_RIDERS = 64;
const TRAINER_SAMPLE_FRESH_MS = 2500;

export interface PowerSampleLike {
  watts: number;
  cadence: number;
  /** Epoch milliseconds. */
  t: number;
}

export interface SelfView {
  edgeId: number;
  s: number;
  lane: number;
  speed: number;
  grade: number;
  draft: number;
  flags: number;
}

export interface RiderVisual {
  id: number;
  isSelf: boolean;
  kit: number;
  pose: Pose;
  speed: number;
  cadence: number;
  distanceSq: number;
}

export interface RideHud {
  power: number;
  cadence: number;
  speedMps: number;
  grade: number;
  draft: number;
  distanceM: number;
  elapsedMs: number;
  averagePower: number;
  connection: ConnectionState;
  connectionDetail: string;
  riderId: number | null;
  paused: boolean;
  source: "trainer" | "sim";
  laps: number;
  race: RaceHud | null;
}

export interface RaceHud {
  raceId: string;
  routeId: number;
  phase: RacePhase;
  startAt: number;
  lap: number;
  laps: number;
  position: number;
  total: number;
  gap: number;
  leaders: RaceLeader[];
  selfStatus?: "racing" | "finished" | "dnf";
}

export interface RideSummary {
  distanceM: number;
  elapsedMs: number;
  averagePower: number;
  laps: number;
  worldVersion: string;
  raceId?: string;
}

interface LocalRider {
  edgeId: number;
  s: number;
  lane: number;
  speed: number;
  grade: number;
}

interface RiderTrack {
  prev: OtherState | null;
  prevAt: number;
  curr: OtherState | null;
  currAt: number;
  lastAt: number;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * Mutable frame state shared by the socket, the HUD poller, and R3F. React
 * state is kept out of the per-frame path on purpose.
 */
export class RideStore {
  readonly world: World;
  route: RouteIndex;
  routeId: number | null;
  readonly startedAt: number;

  simPower = 180;

  connection: ConnectionState = "idle";
  connectionDetail = "Not connected";
  lastError: string | null = null;
  riderId: number | null = null;
  race: RaceHud | null = null;

  private trainerPower: PowerSampleLike | null = null;
  private auth: SelfState | null = null;
  private authAt = 0;
  private local: LocalRider;
  private tracks = new Map<number, RiderTrack>();

  private distanceM = 0;
  private laps = 0;
  private lastOffset: number | null = null;
  private powerSum = 0;
  private powerCount = 0;

  constructor(world: World, spawn: Spawn = world.spawn) {
    this.world = world;
    this.routeId = world.routes[0]?.id ?? null;
    this.route = createRouteIndex(world, this.routeId ?? undefined);
    this.startedAt = Date.now();
    this.local = {
      edgeId: spawn.edge,
      s: spawn.s,
      lane: spawn.lane,
      speed: 0,
      grade: world.sampleGrade(spawn.edge, spawn.s),
    };
  }

  setSimPower(watts: number): void {
    this.simPower = clamp(watts, 0, 500);
  }

  setTrainerPower(sample: PowerSampleLike | null): void {
    this.trainerPower = sample;
  }

  /** Trainer power wins while fresh; otherwise the sim slider keeps the ride alive. */
  getPower(now = Date.now()): number {
    const sample = this.trainerPower;
    if (sample && now - sample.t <= TRAINER_SAMPLE_FRESH_MS) {
      return clamp(sample.watts, 0, 2500);
    }
    return this.simPower;
  }

  getCadence(now = Date.now()): number {
    const sample = this.trainerPower;
    if (sample && now - sample.t <= TRAINER_SAMPLE_FRESH_MS && sample.cadence > 0) {
      return Math.round(clamp(sample.cadence, 0, 250));
    }
    return Math.round(clamp(68 + Math.sqrt(Math.max(0, this.simPower)) * 1.3, 55, 115));
  }

  getPowerSource(now = Date.now()): "trainer" | "sim" {
    const sample = this.trainerPower;
    return sample && now - sample.t <= TRAINER_SAMPLE_FRESH_MS ? "trainer" : "sim";
  }

  applyWelcome(welcome: WelcomeMessage): void {
    this.riderId = welcome.riderId;
    if (!this.auth && !this.race) {
      this.local = {
        edgeId: welcome.spawn.edge,
        s: welcome.spawn.s,
        lane: welcome.spawn.lane,
        speed: this.local.speed,
        grade: this.world.sampleGrade(welcome.spawn.edge, welcome.spawn.s),
      };
    }
  }

  /**
   * Switch the rendered route (free ride -> crit, or between race routes) and
   * reset local progress. The next authoritative snapshot owns the position.
   */
  switchRoute(routeId: number): boolean {
    if (routeId === this.routeId) return true;
    if (!this.world.getRoute(routeId)) {
      this.setError(`The server put this ride on route ${routeId}, which is not in this world.`);
      return false;
    }
    this.routeId = routeId;
    this.route = createRouteIndex(this.world, routeId);
    this.distanceM = 0;
    this.laps = 0;
    this.lastOffset = null;
    this.tracks.clear();
    return true;
  }

  /**
   * Apply a `race_state` control message. Route and spawn changes are applied
   * only on a race/phase transition so the 2 Hz updates never pin the rider to
   * the start line once racing.
   */
  applyRaceState(message: RaceStateMessage): void {
    const previous = this.race;
    const sameRace = previous?.raceId === message.raceId;
    const phaseChanged = !sameRace || previous?.phase !== message.phase;
    const raceStarted =
      message.phase === "racing" && (!sameRace || previous?.phase !== "racing");
    const routeChanged = message.routeId !== this.routeId;
    if (routeChanged) this.switchRoute(message.routeId);

    this.race = {
      raceId: message.raceId,
      routeId: message.routeId,
      phase: message.phase,
      startAt: message.startAt,
      lap: message.lap,
      laps: message.laps,
      position: message.position,
      total: message.total,
      gap: message.gap,
      leaders: message.leaders,
      ...(message.selfStatus ? { selfStatus: message.selfStatus } : {}),
    };

    if (routeChanged || raceStarted || (message.spawn !== undefined && phaseChanged)) {
      this.distanceM = 0;
      this.laps = 0;
      this.lastOffset = null;
    }

    if (message.spawn && (routeChanged || phaseChanged || raceStarted)) {
      const spawn = message.spawn;
      this.local = {
        edgeId: spawn.edge,
        s: spawn.s,
        lane: spawn.lane,
        speed: 0,
        grade: this.world.sampleGrade(spawn.edge, spawn.s),
      };
      this.auth = null;
      this.authAt = 0;
      this.lastOffset = this.route.distanceAt(spawn.edge, spawn.s);
    }
  }

  applySnapshot(snapshot: Snapshot, receivedAt: number): void {
    this.auth = snapshot.self;
    this.authAt = receivedAt;

    this.local = {
      edgeId: snapshot.self.edgeId,
      s: snapshot.self.s,
      lane: snapshot.self.lane,
      speed: snapshot.self.speed,
      grade: snapshot.self.grade,
    };

    this.updateDistance(snapshot.self.edgeId, snapshot.self.s);

    const incoming = new Map<number, OtherState>();
    for (const ring of [snapshot.near, snapshot.mid, snapshot.far]) {
      for (const other of ring) {
        if (other.riderId === this.riderId) continue;
        incoming.set(other.riderId, other);
      }
    }

    for (const [id, other] of incoming) {
      let track = this.tracks.get(id);
      if (!track) {
        track = { prev: null, prevAt: 0, curr: null, currAt: 0, lastAt: 0 };
        this.tracks.set(id, track);
      }
      track.prev = track.curr;
      track.prevAt = track.currAt;
      track.curr = { ...other };
      track.currAt = receivedAt;
      track.lastAt = receivedAt;
    }

    for (const [id, track] of this.tracks) {
      if (receivedAt - track.lastAt > RIDER_TRACK_TTL_MS) {
        this.tracks.delete(id);
      }
    }
  }

  setConnection(state: ConnectionState, detail: string): void {
    this.connection = state;
    this.connectionDetail = detail;
  }

  setError(message: string): void {
    this.lastError = message;
    if (this.connection !== "connected") {
      this.connectionDetail = message;
    }
  }

  recordPower(watts: number): void {
    if (!Number.isFinite(watts)) return;
    this.powerSum += clamp(watts, 0, 2500);
    this.powerCount += 1;
  }

  /**
   * Advance the local-only fallback when no authoritative snapshot is fresh.
   * While the socket is healthy this does nothing: snapshots win.
   */
  frame(now: number, deltaSeconds: number): void {
    const dt = clamp(deltaSeconds, 0, 0.1);
    if (this.auth && now - this.authAt <= SNAPSHOT_FRESH_MS) return;
    if (dt <= 0) return;

    const grade = this.world.sampleGrade(this.local.edgeId, this.local.s);
    const target = solveSpeed(this.getPower(now), grade, 0, DEFAULT_RIDER_CONFIG);
    const acceleration = clamp((target - this.local.speed) * 1.8, -4, 3);
    this.local.speed = Math.max(0, this.local.speed + acceleration * dt);
    this.local.grade = grade;

    const moved = advanceRoute(
      this.world,
      this.local.edgeId,
      this.local.s,
      this.local.speed * dt,
    );
    this.local.edgeId = moved.edgeId;
    this.local.s = moved.s;
    this.updateDistance(moved.edgeId, moved.s);
  }

  /** The local rider as it should be drawn this frame. */
  getSelfView(now: number): SelfView | null {
    if (this.auth && now - this.authAt <= SNAPSHOT_FRESH_MS) {
      const age = Math.min(0.5, Math.max(0, (now - this.authAt) / 1000));
      const advanced = advanceRoute(
        this.world,
        this.auth.edgeId,
        this.auth.s,
        this.auth.speed * age,
      );
      return {
        edgeId: advanced.edgeId,
        s: advanced.s,
        lane: this.auth.lane,
        speed: this.auth.speed,
        grade: this.auth.grade,
        draft: this.auth.draft,
        flags: this.auth.flags,
      };
    }

    const grade = this.local.grade;
    return {
      edgeId: this.local.edgeId,
      s: this.local.s,
      lane: this.local.lane,
      speed: this.local.speed,
      grade,
      draft: 0,
      flags: 0,
    };
  }

  getSelfPose(now: number): Pose | null {
    const self = this.getSelfView(now);
    if (!self) return null;
    return samplePose(this.world, self.edgeId, self.s, self.lane);
  }

  getActualGrade(now = Date.now()): number {
    return this.getSelfView(now)?.grade ?? 0;
  }

  collectRiders(now: number): RiderVisual[] {
    const riders: RiderVisual[] = [];
    const self = this.getSelfView(now);
    const selfPose = self ? samplePose(this.world, self.edgeId, self.s, self.lane) : null;

    if (self && selfPose) {
      riders.push({
        id: this.riderId ?? 0,
        isSelf: true,
        kit: 0,
        pose: selfPose,
        speed: self.speed,
        cadence: this.getCadence(now),
        distanceSq: 0,
      });
    }

    const renderTime = now - INTERPOLATION_DELAY_MS;

    for (const [id, track] of this.tracks) {
      if (now - track.lastAt > RIDER_TRACK_TTL_MS) continue;
      const visual = this.visualForTrack(id, track, renderTime);
      if (!visual) continue;
      if (selfPose) {
        const dx = visual.pose.x - selfPose.x;
        const dz = visual.pose.z - selfPose.z;
        visual.distanceSq = dx * dx + dz * dz;
      }
      riders.push(visual);
    }

    if (riders.length > MAX_RENDERED_RIDERS) {
      riders.sort((a, b) => {
        if (a.isSelf) return -1;
        if (b.isSelf) return 1;
        return a.distanceSq - b.distanceSq;
      });
      riders.length = MAX_RENDERED_RIDERS;
    }

    return riders;
  }

  getHud(now = Date.now()): RideHud {
    const self = this.getSelfView(now);
    return {
      power: this.getPower(now),
      cadence: this.getCadence(now),
      speedMps: self?.speed ?? 0,
      grade: self?.grade ?? 0,
      draft: self?.draft ?? 0,
      distanceM: this.distanceM,
      elapsedMs: Math.max(0, now - this.startedAt),
      averagePower: this.powerCount > 0 ? this.powerSum / this.powerCount : this.getPower(now),
      connection: this.connection,
      connectionDetail: this.connectionDetail,
      riderId: this.riderId,
      paused: self !== null && (self.flags & FLAG_PAUSED) !== 0,
      source: this.getPowerSource(now),
      laps: this.laps,
      race: this.race,
    };
  }

  getSummary(now = Date.now()): RideSummary {
    return {
      distanceM: this.distanceM,
      elapsedMs: Math.max(0, now - this.startedAt),
      averagePower:
        this.powerCount > 0 ? this.powerSum / this.powerCount : this.getPower(now),
      laps: this.laps,
      worldVersion: this.world.version,
      ...(this.race ? { raceId: this.race.raceId } : {}),
    };
  }

  getRouteLength(): number {
    return this.route.lengthM;
  }

  getTrackCount(): number {
    return this.tracks.size;
  }

  private updateDistance(edgeId: number, s: number): void {
    const next = this.route.distanceAt(edgeId, s);
    if (next === null) return;
    if (this.lastOffset !== null) {
      const delta = lapDelta(this.lastOffset, next, this.route.lengthM);
      this.laps = Math.max(0, this.laps + delta);
    }
    this.lastOffset = next;
    this.distanceM = this.laps * this.route.lengthM + next;
  }

  private visualForTrack(
    id: number,
    track: RiderTrack,
    renderTime: number,
  ): RiderVisual | null {
    const curr = track.curr;
    if (!curr) return null;

    const currPose = samplePose(this.world, curr.edgeId, curr.s, curr.lane);
    if (!currPose) return null;

    if (track.prev && track.currAt > track.prevAt) {
      const span = track.currAt - track.prevAt;
      const t = clamp((renderTime - track.prevAt) / span, 0, 1);

      if (renderTime < track.prevAt) {
        return this.visualFromSample(id, track.prev, track.prevAt, renderTime);
      }
      if (renderTime <= track.currAt) {
        if (track.prev.edgeId === curr.edgeId) {
          const pose = samplePose(
            this.world,
            curr.edgeId,
            lerp(track.prev.s, curr.s, t),
            lerp(track.prev.lane, curr.lane, t),
          );
          if (pose) {
            return {
              id,
              isSelf: false,
              kit: curr.kit,
              pose,
              speed: lerp(track.prev.speed, curr.speed, t),
              cadence: this.getCadence(),
              distanceSq: 0,
            };
          }
        } else {
          const prevPose = samplePose(this.world, track.prev.edgeId, track.prev.s, track.prev.lane);
          if (prevPose) {
            return {
              id,
              isSelf: false,
              kit: curr.kit,
              pose: {
                x: lerp(prevPose.x, currPose.x, t),
                y: lerp(prevPose.y, currPose.y, t),
                z: lerp(prevPose.z, currPose.z, t),
                heading: currPose.heading,
              },
              speed: lerp(track.prev.speed, curr.speed, t),
              cadence: this.getCadence(),
              distanceSq: 0,
            };
          }
        }
      }

      if (renderTime > track.currAt) {
        const age = Math.min(0.5, (renderTime - track.currAt) / 1000);
        const advanced = advanceRoute(this.world, curr.edgeId, curr.s, curr.speed * age);
        const pose = samplePose(this.world, advanced.edgeId, advanced.s, curr.lane);
        if (pose) {
          return {
            id,
            isSelf: false,
            kit: curr.kit,
            pose,
            speed: curr.speed,
            cadence: this.getCadence(),
            distanceSq: 0,
          };
        }
      }
    }

    return {
      id,
      isSelf: false,
      kit: curr.kit,
      pose: currPose,
      speed: curr.speed,
      cadence: this.getCadence(),
      distanceSq: 0,
    };
  }

  private visualFromSample(
    id: number,
    sample: OtherState,
    _sampleAt: number,
    _renderTime: number,
  ): RiderVisual | null {
    const pose = samplePose(this.world, sample.edgeId, sample.s, sample.lane);
    if (!pose) return null;
    return {
      id,
      isSelf: false,
      kit: sample.kit,
      pose,
      speed: sample.speed,
      cadence: this.getCadence(),
      distanceSq: 0,
    };
  }
}
